import React, { useState } from 'react';
import SpecsNote from '../common/SpecsNote';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, MapPin, MessageCircle } from 'lucide-react';
import seriesContent from '../../content/seriesContent.json';
import seo from '../../seo/seoConfig.json';
import TitleText from '../common/TitleText';

const WHATSAPP = 'https://wa.me/18297163555?text=';

const fadeUp = {
    hidden: { opacity: 0, y: 40 },
    show: { opacity: 1, y: 0, transition: { duration: 0.7, ease: 'easeOut' } }
};

const FaqItem = ({ item, open, onToggle }) => (
    <div className="border-b border-zinc-200">
        <button
            type="button"
            onClick={onToggle}
            aria-expanded={open}
            className="w-full flex items-center justify-between gap-6 py-6 text-left group"
        >
            <h3 className="text-lg md:text-xl font-bold text-black group-hover:text-zinc-600 transition-colors">{item.q}</h3>
            <Plus className={`w-6 h-6 shrink-0 transition-transform duration-300 ${open ? 'rotate-45' : ''}`} />
        </button>
        <AnimatePresence initial={false}>
            {open && (
                <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className="overflow-hidden"
                >
                    <p className="pb-6 text-gray-600 leading-relaxed max-w-3xl">{item.a}</p>
                </motion.div>
            )}
        </AnimatePresence>
    </div>
);

// Official Raymon series copy in Spanish (src/content/seriesContent.json) with highlights and FAQ.
// faqOnly: for pages that already have their own long-form copy (Tarok, Durok).
const SeriesInfo = ({ slug, images = [], faqOnly = false }) => {
    const content = seriesContent[slug];
    const [openFaq, setOpenFaq] = useState(0);
    if (!content) return null;

    const [mainImage, secondImage] = images;
    const whatsappText = encodeURIComponent(`Hola, me interesa la Raymon ${content.nombre}. ¿Me pueden dar más información?`);

    return (
        <>
            {/* About the series */}
            {!faqOnly && <section className="max-w-[1600px] mx-auto px-6 md:px-16 py-24 overflow-hidden">
                <div className={`grid grid-cols-1 ${mainImage ? 'lg:grid-cols-2' : ''} gap-16 items-center`}>
                    <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.3 }} className="space-y-6">
                        <span className="inline-block py-1 px-3 border border-black/20 text-xs font-bold uppercase tracking-[0.2em]">
                            Raymon {content.nombre}{content.tipo ? ` · ${content.tipo}` : ''}
                        </span>
                        <h2 className="text-4xl md:text-5xl font-black italic text-black leading-tight tracking-tight">
                            <TitleText text={content.eslogan} words={2} dark />
                        </h2>
                        <p className="text-xl text-gray-800 leading-relaxed">{content.intro}</p>
                        {content.historia && (
                            <p className="text-lg text-gray-600 leading-relaxed">{content.historia}</p>
                        )}
                    </motion.div>
                    {mainImage && (
                        <motion.div
                            initial={{ opacity: 0, x: 60 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true, amount: 0.3 }}
                            transition={{ duration: 0.8, ease: 'easeOut' }}
                            className="flex justify-center lg:justify-end"
                        >
                            <img loading="lazy" decoding="async" src={mainImage} alt={`Raymon ${content.nombre}`} className="w-full max-w-[700px] h-auto object-cover rounded-2xl" />
                        </motion.div>
                    )}
                </div>
            </section>}

            {/* Highlights */}
            {!faqOnly && content.destacados?.length > 0 && (
                <section className="relative bg-zinc-950 text-white py-24 overflow-hidden">
                    {secondImage && (
                        <img loading="lazy" decoding="async" src={secondImage} alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-cover opacity-15" />
                    )}
                    <div className="relative max-w-[1400px] mx-auto px-6 md:px-16">
                        <motion.h2 variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} className="text-3xl md:text-5xl font-black uppercase italic tracking-tight mb-16">
                            Lo que hace especial a la <span className="gradient-text">{content.nombre}</span>
                        </motion.h2>
                        <motion.div
                            initial="hidden"
                            whileInView="show"
                            viewport={{ once: true, amount: 0.2 }}
                            transition={{ staggerChildren: 0.15 }}
                            className={`grid grid-cols-1 md:grid-cols-2 ${content.destacados.length > 2 ? 'lg:grid-cols-3' : ''} gap-6`}
                        >
                            {content.destacados.map((item, index) => (
                                <motion.div
                                    key={item.titulo}
                                    variants={fadeUp}
                                    className="border border-white/15 bg-white/5 backdrop-blur-sm rounded-2xl p-8 hover:border-white/40 hover:-translate-y-1 transition-all duration-300"
                                >
                                    <div className="text-sm font-black text-zinc-500 mb-6">{String(index + 1).padStart(2, '0')}</div>
                                    <h3 className="text-xl font-black italic uppercase tracking-tight mb-4">{item.titulo}</h3>
                                    <p className="text-zinc-300 leading-relaxed">{item.texto}</p>
                                </motion.div>
                            ))}
                        </motion.div>
                    </div>
                </section>
            )}

            {/* FAQ + contact */}
            <section className="max-w-[1400px] mx-auto px-6 md:px-16 py-24">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-16">
                    <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} className="space-y-6">
                        <h2 className="text-3xl md:text-4xl font-black italic uppercase tracking-tight leading-none">
                            {content.faq?.length ? <>Preguntas<br /><span className="gradient-text-dark">frecuentes</span></> : <>¿Quieres<br /><span className="gradient-text-dark">probarla?</span></>}
                        </h2>
                        <p className="text-gray-600 leading-relaxed">
                            Ven a verla a nuestra tienda en {seo.business.streetAddress}, {seo.business.addressLocality}, o escríbenos y te ayudamos a elegir el modelo y la talla.
                        </p>
                        <SpecsNote />
                        <div className="flex flex-col sm:flex-row lg:flex-col gap-3">
                            <a href={`${WHATSAPP}${whatsappText}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-3 bg-black text-white px-6 py-4 text-xs font-bold uppercase tracking-widest hover:bg-zinc-800 transition-colors">
                                <MessageCircle className="w-4 h-4" /> Escribir por WhatsApp
                            </a>
                            <a href={seo.business.mapsUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-3 border border-black px-6 py-4 text-xs font-bold uppercase tracking-widest hover:bg-black hover:text-white transition-colors">
                                <MapPin className="w-4 h-4" /> Cómo llegar
                            </a>
                        </div>
                    </motion.div>
                    {content.faq?.length > 0 && (
                        <div className="lg:col-span-2">
                            {content.faq.map((item, index) => (
                                <FaqItem key={item.q} item={item} open={openFaq === index} onToggle={() => setOpenFaq(openFaq === index ? -1 : index)} />
                            ))}
                        </div>
                    )}
                </div>
            </section>
        </>
    );
};

export default SeriesInfo;
